import { computed, type ComputedRef } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import { storeToRefs } from "pinia";

/**
 * Composable for accessing feature flags in components.
 * Provides reactive access to feature flag values with type-safe helpers.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * const { isEnabled, getBoolean, getString } = useFeatureFlags();
 *
 * // Check if a feature is enabled
 * const showNewDashboard = isEnabled('show_new_dashboard');
 *
 * // Get typed values
 * const maxUploadSize = getNumber('max_upload_size', 10);
 * const welcomeMessage = getString('welcome_message', 'Hello!');
 * </script>
 * ```
 */
export function useFeatureFlags() {
  const channelStore = useChannelStore();
  const { featureFlags, featureFlagsLoaded } = storeToRefs(channelStore);

  /**
   * Get a feature flag value as any type.
   * Returns a computed ref that updates when the flag changes.
   */
  function get<T = unknown>(
    key: string,
    defaultValue: T,
  ): ComputedRef<T | undefined> {
    return computed(() => channelStore.getFeatureFlag<T>(key, defaultValue));
  }

  /**
   * Get a boolean feature flag value.
   * Returns a computed ref with the boolean value (defaults to false).
   */
  function getBoolean(key: string, defaultValue = false): ComputedRef<boolean> {
    return computed(() => {
      const value = channelStore.getFeatureFlag(key, defaultValue);
      return value ?? defaultValue;
    });
  }

  /**
   * Get a string feature flag value.
   * Returns a computed ref with the string value.
   */
  function getString(key: string, defaultValue = ""): ComputedRef<string> {
    return computed(() => channelStore.getFeatureFlag(key, defaultValue));
  }

  /**
   * Get a number feature flag value.
   * Returns a computed ref with the number value.
   */
  function getNumber(key: string, defaultValue = 0): ComputedRef<number> {
    return computed(() => channelStore.getFeatureFlag(key, defaultValue));
  }

  /**
   * Get a JSON feature flag value.
   * Returns a computed ref with the parsed JSON value.
   */
  function getJSON<T = Record<string, unknown>>(
    key: string,
    defaultValue?: T,
  ): ComputedRef<T | undefined> {
    return computed(() => {
      const value = channelStore.getFeatureFlag<T | undefined>(
        key,
        defaultValue,
      );
      return value ?? defaultValue;
    });
  }

  /**
   * Refresh feature flags from the server.
   */
  async function refresh(): Promise<void> {
    await channelStore.refreshFeatureFlags();
  }

  return {
    // State
    flags: featureFlags,
    loaded: featureFlagsLoaded,

    // Getters
    get,
    getBoolean,
    getString,
    getNumber,
    getJSON,

    // Actions
    refresh,
  };
}
